import React, {
  useCallback,
  useRef,
  useState,
} from "react";
import { queryCache } from "react-query";
import clsx from "clsx";
import ArrowRightIcon from "@material-ui/icons/ArrowRight";
import OverflowTooltip from "../../../components/OverflowTooltip/OverflowTooltip";
import { ActionBar } from "../../../components/ActionBar/ActionBar";
import { QUERY_KEYS } from "../../../constants";
import { useWizard } from "../../wizard/context";
import { useAcademicsHigherContext } from "../pages/AcademicsPageHigher";
import { useAcademicStyles } from "../styles/useAcademicStyles";
import { IAcademicListItemHigher } from "../types";
import { ProgramHigher } from "./program/ProgramHigher";
import { Accordion, AccordionDetails, AccordionSummary } from "./Accordion";
import { getBeautifiedCount } from "../../../helpers";

export function AcademicHigher({
  academic,
  highlighted,
}: {
  academic: IAcademicListItemHigher;
  highlighted: boolean;
}) {
  const classes = useAcademicStyles();

  const { openWizard } = useWizard();

  const { academicWizardConfigs, programWizardConfigs, refetchAcademics } =
    useAcademicsHigherContext();

  const { schoolStudentApplications, majorsContainingPrograms, degrees } =
    QUERY_KEYS;

  const cloneProgram = useCallback(async (programId: string) => {
    (await openWizard(programWizardConfigs.getCloneConfig!(programId))) &&
      (await refetchAcademics());
    queryCache.invalidateQueries([majorsContainingPrograms]);
  }, []);

  const createProgram = useCallback(async () => {
    const result = await openWizard(
      programWizardConfigs.getCreateConfig(academic.id),
    );
    if (result) {
      await refetchAcademics();
      await queryCache.invalidateQueries([majorsContainingPrograms]);
      await queryCache.invalidateQueries([degrees]);

      if (result.isAutoClone) {
        cloneProgram(result.id);
      }
    }
  }, [academic.id, openWizard, programWizardConfigs, refetchAcademics]);

  const changeAcademicStatus = useCallback(async () => {
    if (academic.isActive) {
      if (
        await openWizard(academicWizardConfigs.getDeactivateConfig(academic.id))
      ) {
        await refetchAcademics();
        queryCache.invalidateQueries([schoolStudentApplications]);
      }
    } else {
      if (
        await openWizard(academicWizardConfigs.getActivateConfig(academic.id))
      ) {
        await refetchAcademics();
      }
    }
  }, [
    academic.id,
    academic.isActive,
    academicWizardConfigs,
    openWizard,
    refetchAcademics,
  ]);

  const viewAcademic = useCallback(
    () => openWizard(academicWizardConfigs.getViewConfig(academic.id)),
    [academic.id, academicWizardConfigs, openWizard],
  );
  const editAcademic = useCallback(
    () => openWizard(academicWizardConfigs.getEditConfig(academic.id)),
    [academic.id, academicWizardConfigs, openWizard],
  );

  const deleteAcademic = useCallback(async () => {
    if (await openWizard(academicWizardConfigs.getDeleteConfig(academic.id))) {
      await refetchAcademics();
      await queryCache.invalidateQueries([degrees]);
    }
  }, [academic.id, academicWizardConfigs, openWizard, refetchAcademics]);

  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [isExpandedWithDelay, setIsExpandedWithDelay] =
    useState<boolean>(false);

  const timeoutRef = useRef<any>(null);
  const handleExpandChange = useCallback(() => {
    setIsExpanded((prev) => !prev);

    clearTimeout(timeoutRef.current);

    if (isExpanded) {
      timeoutRef.current = setTimeout(() => {
        setIsExpandedWithDelay(false);
      }, 1000);
    } else {
      setIsExpandedWithDelay(true);
    }
  }, []);

  return (
    <Accordion expanded={isExpanded} onChange={handleExpandChange}>
      <AccordionSummary
        className={clsx(highlighted && classes.highlightedSummary)}
        expandIcon={<ArrowRightIcon />}
        aria-controls="panel1a-content"
        id="panel1a-header"
      >
        <div className={classes.infoContainer}>
          <div className={classes.name}>
            <OverflowTooltip text={academic.degreeName} />
          </div>
          <div className={classes.subName}>Degree</div>
          <div className={classes.programsCount}>
            {`(${getBeautifiedCount(academic.programsCount, "program")})`}
          </div>
        </div>
        <ActionBar
          createCallBack={createProgram}
          createTooltip="Add program"
          viewCallBack={viewAcademic}
          viewTooltip="View academic"
          editCallBack={editAcademic}
          editTooltip="Edit academic"
          switchCallBack={changeAcademicStatus}
          switchTooltipActive="Deactivate academic"
          switchTooltipNotActive="Activate academic"
          switchState={academic.isActive}
          deleteCallBack={deleteAcademic}
          deleteTooltip="Delete academic"
        />
      </AccordionSummary>
      <AccordionDetails>
        <div
          className={clsx(
            classes.accordionDetails,
            !academic.isActive && classes.disabled,
          )}
        >
          {isExpandedWithDelay &&
            academic.programs.map((program) => (
              <ProgramHigher
                key={program.id}
                programBackEnd={program}
                degreeName={academic.degreeName}
              />
            ))}
        </div>
      </AccordionDetails>
    </Accordion>
  );
}
